import React, { useState, useEffect, useCallback, useRef, memo, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Table,
  Tag,
  Input,
  Select,
  Space,
  Modal,
  Form,
  message,
  Popconfirm,
  Tooltip,
  Pagination,
  Spin,
  Typography,
  Dropdown,
  FloatButton,
  BackTop,
  Result,
  DatePicker,
} from 'antd';
import type { TableProps } from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  DownOutlined,
  CopyOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  FilterOutlined,
  CheckSquareOutlined,
  DatabaseOutlined,
  DownloadOutlined,
  GiftOutlined,
} from '@ant-design/icons';
import { dataAPI, categoryAPI, authAPI } from '../services/api';
import { copyToClipboard as clipboardCopy } from '../utils/clipboard';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/zh-cn';
import utc from 'dayjs/plugin/utc';
import zhCN from 'antd/locale/zh_CN';
import CategoryManager from '../components/CategoryManager';
import { useAuth } from '../contexts/AuthContext';

// 设置dayjs默认语言为中文
dayjs.locale('zh-cn');
// 启用 utc 插件
dayjs.extend(utc);

// 处理 zhCN 导入问题（某些构建配置可能返回 default 属性）
const locale = (zhCN && typeof zhCN === 'object' && 'default' in zhCN) 
  ? (zhCN as any).default 
  : zhCN;

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;
const { RangePicker } = DatePicker;

const FilterSelect = memo(({ 
  placeholder, 
  value, 
  onChange, 
  loading, 
  children, 
  style 
}: {
  placeholder: string;
  value: string | undefined;
  onChange: (value: string) => void;
  loading: boolean;
  children: React.ReactNode;
  style: React.CSSProperties;
}) => (
  <Select
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    style={style}
    allowClear
    loading={loading}
    notFoundContent={loading ? <Spin size="small" /> : '暂无数据'}
  >
    {children}
  </Select>
));

interface DataItem {
  id: number;
  content: string;
  status: 'unused' | 'pending' | 'used' | 'processing';
  notes?: string;
  category_id?: number;
  category_name?: string;
  category_is_default?: boolean;
  created_at: string;
  updated_at: string;
}

interface Stats {
  total: number;
  unused: number;
  pending: number;
  used: number;
  processing: number;
}

interface Category {
  id: number;
  name: string;
  description?: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

interface CheckinStatus {
  enabled: boolean;
  checkedInToday: boolean;
  rewardDaysToday: number | null;
  nextAvailableAt: string | null;
  autoCloseModal?: boolean;
  autoCloseDelay?: number;
}

interface CheckinResultUser {
  id: number;
  username: string;
  email: string;
  is_admin?: boolean;
  is_active?: boolean;
  expires_at?: string | null;
}

interface CheckinResult {
  rewardDays?: number | null;
  checkedInAt?: string | null;
  nextAvailableAt?: string | null;
  alreadyCheckedIn?: boolean;
  expiresAt?: string | null;
  user?: CheckinResultUser | null;
  autoCloseModal?: boolean;
  autoCloseDelay?: number;
}

const Dashboard: React.FC = () => {
  const { updateUser } = useAuth();
  const [data, setData] = useState<DataItem[]>([]);
  const [stats, setStats] = useState<Stats>({ total: 0, unused: 0, pending: 0, used: 0, processing: 0 });
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    status: [] as string[],
    search: '',
    notes_search: '',
    category_id: '',
    empty_notes: '',
    created_from: '',
    created_to: '',
    sort_by: '',
    sort_order: '',
  });
  
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const autoRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const autoCloseModalTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<DataItem | null>(null);
  const [batchStatusValue, setBatchStatusValue] = useState<string | undefined>(undefined);
  const [form] = Form.useForm();
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addForm] = Form.useForm();
  const [isAddingData, setIsAddingData] = useState(false);
  const [categoryManagerVisible, setCategoryManagerVisible] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(30); // 秒
  const [countdown, setCountdown] = useState(0);
  const [batchStatusModalVisible, setBatchStatusModalVisible] = useState(false);
  const [selectedStatusType, setSelectedStatusType] = useState<{ fromStatus: string[]; toStatus: string }>({ fromStatus: [], toStatus: '' });
  const [downloading, setDownloading] = useState(false);
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [checkinStatus, setCheckinStatus] = useState<CheckinStatus>({
    enabled: false,
    checkedInToday: false,
    rewardDaysToday: null,
    nextAvailableAt: null,
  });
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [checkinModalVisible, setCheckinModalVisible] = useState(false);
  const [checkinResult, setCheckinResult] = useState<CheckinResult | null>(null);
  const [createdRange, setCreatedRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [tableSorter, setTableSorter] = useState<{ field?: string; order?: 'ascend' | 'descend' }>({});

  const formatExpiryTime = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const hours = dateObj.getHours();
    const minutes = dateObj.getMinutes();
    const seconds = dateObj.getSeconds();
    
    if (hours === 0 && minutes === 0 && seconds === 0) {
      return dateObj.toLocaleDateString();
    }
    
    return dateObj.toLocaleString();
  };

  const _getStatusTag = (status: string) => {
    const statusConfig = {
      'unused': { color: 'default', icon: <ClockCircleOutlined />, text: '未使用' },
      'pending': { color: 'orange', icon: <ClockCircleOutlined />, text: '待使用' },
      'used': { color: 'green', icon: <CheckCircleOutlined />, text: '已使用' },
      'processing': { color: 'blue', icon: <ExclamationCircleOutlined />, text: '处理中' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig];
    if (!config) return status;
    
    return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
  };

  const getStatusText = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'unused': '未使用',
      'pending': '待使用',
      'used': '已使用',
      'processing': '处理中'
    };
    
    return statusMap[status] || status;
  };

  const copyToClipboard = async (text: string, type: string = '内容') => {
    const ok = await clipboardCopy(text);
    if (ok) message.success(`${type}已复制到剪贴板`);
    else message.error('复制失败，请手动复制');
  };

  const buildFilterParams = useCallback((overrides: Record<string, any> = {}) => {
    const params: Record<string, any> = { ...overrides };

    const setParamIfAbsent = (key: string, value: any) => {
      if ((value === 0 || value) && params[key] === undefined) {
        params[key] = value;
      }
    };

    if (filters.status.length > 0 && params.status === undefined) {
      params.status = filters.status.join(',');
    }
    setParamIfAbsent('search', filters.search || undefined);
    setParamIfAbsent('notes_search', filters.notes_search || undefined);
    if (filters.category_id) {
      const parsedCategoryId = parseInt(filters.category_id, 10);
      if (!Number.isNaN(parsedCategoryId)) {
        setParamIfAbsent('category_id', parsedCategoryId);
      }
    }
    setParamIfAbsent('empty_notes', filters.empty_notes || undefined);
    setParamIfAbsent('created_from', filters.created_from || undefined);
    setParamIfAbsent('created_to', filters.created_to || undefined);
    if (filters.sort_by && filters.sort_order) {
      if (params.sort_by === undefined) {
        params.sort_by = filters.sort_by;
      }
      if (params.sort_order === undefined) {
        params.sort_order = filters.sort_order;
      }
    }

    return params;
  }, [filters]);

  const fetchData = useCallback(async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const response = await dataAPI.getData(
        buildFilterParams({
        page,
        limit: pageSize,
        })
      );
      let dataItems = response.data?.data;
      if (!Array.isArray(dataItems)) {
        console.error('API返回的data不是数组:', dataItems);
        dataItems = [];
      }
      setData(dataItems);
      setPagination(prev => ({
        ...prev,
        current: page,
        pageSize,
        total: response.data?.pagination?.total || 0,
      }));
    } catch (error: any) {
      message.error(error.response?.data?.error || '获取数据失败');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams]);

  const fetchDataAndAdjustPage = async () => {
    setLoading(true);
    try {
      const response = await dataAPI.getData(
        buildFilterParams({
        page: pagination.current,
        limit: pagination.pageSize,
        })
      );
      
      let dataItems = response.data?.data;
      if (!Array.isArray(dataItems)) {
        console.error('API返回的data不是数组:', dataItems);
        dataItems = [];
      }
      const paginationInfo = response.data?.pagination || { total: 0 };
      const totalPages = Math.ceil(paginationInfo.total / pagination.pageSize);
      
      let targetPage = pagination.current;
      if (pagination.current > totalPages && totalPages > 0) {
        targetPage = totalPages;
      }
      
      if (targetPage !== pagination.current) {
        const adjustedResponse = await dataAPI.getData(
          buildFilterParams({
          page: targetPage,
          limit: pagination.pageSize,
          })
        );
        let adjustedDataItems = adjustedResponse.data?.data;
        if (!Array.isArray(adjustedDataItems)) {
          console.error('API返回的data不是数组:', adjustedDataItems);
          adjustedDataItems = [];
        }
        setData(adjustedDataItems);
        setPagination(prev => ({
          ...prev,
          current: targetPage,
          total: adjustedResponse.data?.pagination?.total || 0,
        }));
      } else {
        setData(dataItems);
        setPagination(prev => ({
          ...prev,
          current: pagination.current,
          total: paginationInfo.total,
        }));
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || '获取数据失败');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const categoryId = filters.category_id ? parseInt(filters.category_id) : undefined;
      const response = await dataAPI.getStats(categoryId);
      if (response.data.status === 200) {
        setStats(response.data.data);
      } else {
        console.error('获取统计数据失败:', response.data.error);
      }
    } catch (error: any) {
      console.error('获取统计数据失败:', error);
    }
  };

  const formatRewardDays = (value: any): number | null => {
    if (value === null || value === undefined) {
      return null;
    }
    const num = Number(value);
    if (!Number.isFinite(num)) {
      return null;
    }
    return Number(num.toFixed(2));
  };

  const renderRewardDaysText = (value: number | null | undefined): string => {
    if (value === null || value === undefined) {
      return '';
    }
    const formatted = formatRewardDays(value);
    if (formatted === null) {
      return '';
    }
    return Number.isInteger(formatted) ? formatted.toString() : formatted.toFixed(2);
  };

  const fetchCheckinStatus = useCallback(async () => {
    try {
      const response = await authAPI.getCheckinStatus();
      if (response.data.status === 200) {
        const data = response.data.data || {};
        const newStatus = {
          enabled: Boolean(data.enabled),
          checkedInToday: Boolean(data.checkedInToday),
          rewardDaysToday: formatRewardDays(data.rewardDaysToday),
          nextAvailableAt: data.nextAvailableAt ?? null,
          autoCloseModal: Boolean(data.autoCloseModal),
          autoCloseDelay: typeof data.autoCloseDelay === 'number' ? data.autoCloseDelay : 3,
        };
        setCheckinStatus(newStatus);
        // 调试信息：检查签到状态
        if (process.env.NODE_ENV === 'development') {
          console.log('签到状态更新:', newStatus);
        }
      } else {
        console.error('获取签到状态失败:', response.data.error);
      }
    } catch (error) {
      console.error('获取签到状态失败:', error);
    }
  }, []);

  const handleCreatedRangeChange = (dates: null | [Dayjs | null, Dayjs | null]) => {
    setCreatedRange(dates);
    setFilters(prev => ({
      ...prev,
      // 直接发送北京时间格式的字符串，后端会直接使用该字符串进行查询
      created_from: dates && dates[0] ? dates[0].utcOffset(480).startOf('second').format('YYYY-MM-DD HH:mm:ss') : '',
      created_to: dates && dates[1] ? dates[1].utcOffset(480).endOf('second').format('YYYY-MM-DD HH:mm:ss') : '',
    }));
  };

  const debouncedFetchData = useCallback((newFilters: typeof filters) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    setFilterLoading(true);
    
    debounceTimerRef.current = setTimeout(async () => {
      try {
        await fetchData(1, pagination.pageSize);
        await fetchStats();
      } finally {
        setFilterLoading(false);
      }
    }, 300); // 300ms 防抖延迟
  }, [fetchData, pagination.pageSize]);

  const categoryOptions = useMemo(() => 
    categories.map(category => (
      <Option key={category.id} value={category.id.toString()}>
        {category.is_default ? (
          <Tag color="blue" icon={<ExclamationCircleOutlined />}>
            默认
          </Tag>
        ) : (
          <Tag color="green" icon={<FileTextOutlined />}>
            {category.name}
          </Tag>
        )}
      </Option>
    )), [categories]
  );

  const statusOptions = useMemo(() => (
    <>
      <Option value="unused">
        <Tag color="default" icon={<ClockCircleOutlined />}>
          未使用
        </Tag>
      </Option>
      <Option value="pending">
        <Tag color="orange" icon={<ClockCircleOutlined />}>
          待使用
        </Tag>
      </Option>
      <Option value="used">
        <Tag color="green" icon={<CheckCircleOutlined />}>
          已使用
        </Tag>
      </Option>
      <Option value="processing">
        <Tag color="blue" icon={<ExclamationCircleOutlined />}>
          处理中
        </Tag>
      </Option>
    </>
  ), []);

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getCategories();
      if (response.data.status === 200) {
        setCategories(response.data.data);
      }
    } catch (error: any) {
      console.error('获取分类列表失败:', error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchStats();
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchCheckinStatus();
    
    // 定期刷新签到状态（每30秒），以便在管理员启用签到功能后能及时显示
    const interval = setInterval(() => {
      fetchCheckinStatus();
    }, 30000);
    
    // 当页面可见时刷新签到状态
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchCheckinStatus();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchCheckinStatus]);

  useEffect(() => {
    debouncedFetchData(filters);
  }, [filters, debouncedFetchData]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (autoCloseModalTimerRef.current) {
        clearTimeout(autoCloseModalTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (autoCloseModalTimerRef.current) {
      clearTimeout(autoCloseModalTimerRef.current);
      autoCloseModalTimerRef.current = null;
    }

    if (checkinModalVisible && checkinResult && !checkinResult.alreadyCheckedIn) {
      const autoCloseModal = checkinResult.autoCloseModal !== undefined 
        ? Boolean(checkinResult.autoCloseModal)
        : (checkinStatus.autoCloseModal ?? false);
      const autoCloseDelay = checkinResult.autoCloseDelay !== undefined
        ? Number(checkinResult.autoCloseDelay)
        : (checkinStatus.autoCloseDelay ?? 3);
      
      if (autoCloseModal) {
        const delay = Math.max(1, autoCloseDelay) * 1000;
        autoCloseModalTimerRef.current = setTimeout(() => {
          setCheckinModalVisible(false);
          setCheckinResult(null);
        }, delay);
      }
    }

    return () => {
      if (autoCloseModalTimerRef.current) {
        clearTimeout(autoCloseModalTimerRef.current);
        autoCloseModalTimerRef.current = null;
      }
    };
  }, [checkinModalVisible, checkinResult, checkinStatus.autoCloseModal, checkinStatus.autoCloseDelay]);

  const handleUpload = async (file: File, categoryId?: number) => {
    setUploading(true);
    try {
      const response = await dataAPI.uploadFile(file, categoryId);
      const { success, errors, total } = response.data;
      
      if (success > 0) {
        message.success(`文件上传成功，成功导入 ${success} 条有效数据${errors > 0 ? `，${errors} 条失败` : ''}`);
      } else {
        message.warning('文件上传完成，但没有有效数据被导入');
      }
      
      fetchData(pagination.current, pagination.pageSize);
      fetchStats();
    } catch (error: any) {
      message.error(error.response?.data?.error || '文件上传失败');
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleTableChange: TableProps<DataItem>['onChange'] = (_pagination, _filters, sorterParam) => {
    const sorterArray = Array.isArray(sorterParam) ? sorterParam : [sorterParam];
    const sorter = sorterArray[0];
    const sortField = (sorter?.field as string) || '';
    const sortOrder = sorter?.order as ('ascend' | 'descend' | undefined);
    const sortableFields = ['id', 'created_at'];
    
    setTableSorter({
      field: sortField || undefined,
      order: sortOrder,
    });
    
    if (sortField && sortOrder && sortableFields.includes(sortField)) {
      setFilters(prev => ({
        ...prev,
        sort_by: sortField,
        sort_order: sortOrder === 'ascend' ? 'asc' : 'desc',
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        sort_by: '',
        sort_order: '',
      }));
    }
  };

  const handleUpdate = async (values: { status: string; notes: string }) => {
    if (!editingItem) return;

    try {
      const response = await dataAPI.updateData(editingItem.id, values);
      message.success(response.data.message || '更新成功');
      setEditModalVisible(false);
      setEditingItem(null);
      form.resetFields();
      fetchData(pagination.current, pagination.pageSize);
      fetchStats();
    } catch (error: any) {
      console.error('更新错误:', error);
      console.error('错误响应:', error.response);
      message.error(error.response?.data?.error || '更新失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await dataAPI.deleteData(id);
      message.success('删除成功');
      await fetchDataAndAdjustPage();
      fetchStats();
    } catch (error: any) {
      console.error('删除失败:', error);
      console.error('错误详情:', error.response?.data);
      message.error(error.response?.data?.error || error.message || '删除失败');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的数据');
      return;
    }

    try {
      await dataAPI.batchDeleteData(selectedRowKeys as number[]);
      message.success(`成功删除 ${selectedRowKeys.length} 条数据`);
      setSelectedRowKeys([]);
      await fetchDataAndAdjustPage();
      fetchStats();
    } catch (error: any) {
      message.error(error.response?.data?.error || '批量删除失败');
    }
  };

  const handleBatchUpdateStatus = async (status: string, notes?: string) => {
    
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要更新的数据');
      return;
    }

    try {
      const response = await dataAPI.batchUpdateData(selectedRowKeys as number[], { status, notes });
      
      message.success(response.data.message || `成功更新 ${selectedRowKeys.length} 条数据状态为 ${status === 'unused' ? '未使用' : status === 'used' ? '已使用' : '处理中'}`);
      setSelectedRowKeys([]);
      setBatchStatusValue(undefined);
      
      await fetchData(pagination.current, pagination.pageSize);
      await fetchStats();
    } catch (error: any) {
      console.error('Batch update error:', error);
      message.error(error.response?.data?.error || '批量更新失败');
    }
  };

  const handleDeleteAllData = async () => {
    try {
      const categoryId = filters.category_id ? parseInt(filters.category_id) : undefined;
      const response = await dataAPI.deleteAllData(categoryId);
      message.success(response.data.message || '数据删除成功');
      
      // 重新获取数据和统计
      await fetchData(1, pagination.pageSize);
      await fetchStats();
    } catch (error: any) {
      console.error('Delete all data error:', error);
      message.error(error.response?.data?.error || '删除数据失败');
    }
  };

  // 按状态删除数据
  const handleDeleteByStatus = async (status: string) => {
    try {
      const categoryId = filters.category_id ? parseInt(filters.category_id) : undefined;
      const response = await dataAPI.deleteDataByStatus(status, categoryId);
      message.success(response.data.message || '删除成功');
      
      // 重新获取数据和统计
      await fetchData(1, pagination.pageSize);
      await fetchStats();
    } catch (error: any) {
      message.error(error.response?.data?.error || '删除失败');
    }
  };

  // 批量设置未使用数据为待使用状态
  const handleBatchSetPending = async () => {
    try {
      const categoryId = filters.category_id ? parseInt(filters.category_id) : undefined;
      const response = await dataAPI.batchSetPending(categoryId);
      message.success(response.data.message || '批量设置成功');
      
      // 重新获取数据和统计
      await fetchData(pagination.current, pagination.pageSize);
      await fetchStats();
    } catch (error: any) {
      message.error(error.response?.data?.error || '批量设置失败');
    }
  };

  // 批量修改状态
  const handleBatchStatusChange = async () => {
    try {
      if (!selectedStatusType.fromStatus.length || !selectedStatusType.toStatus) {
        message.warning('请选择源状态和目标状态');
        return;
      }
      
      if (selectedStatusType.fromStatus.includes(selectedStatusType.toStatus)) {
        message.warning('源状态中不能包含目标状态');
        return;
      }
      
      setBatchStatusModalVisible(false);
      
      const statusMap: { [key: string]: string } = {
        'unused': '未使用',
        'pending': '待使用', 
        'used': '已使用',
        'processing': '处理中'
      };
      
      const fromStatusTexts = selectedStatusType.fromStatus.map(status => statusMap[status] || status).join('、');
      const toStatusText = statusMap[selectedStatusType.toStatus] || selectedStatusType.toStatus;
      
      // 构建API参数，如果有筛选条件则只对筛选的数据进行操作
      const apiParams: any = {
        from_status: selectedStatusType.fromStatus,
        to_status: selectedStatusType.toStatus
      };
      
      // 如果有筛选条件，添加筛选参数
      if (filters.category_id || filters.status.length > 0 || filters.empty_notes || filters.search || filters.notes_search) {
        if (filters.category_id) apiParams.category_id = parseInt(filters.category_id);
        if (filters.status.length > 0) apiParams.status = filters.status.join(',');
        if (filters.empty_notes) apiParams.empty_notes = filters.empty_notes;
        if (filters.search) apiParams.content_search = filters.search;
        if (filters.notes_search) apiParams.notes_search = filters.notes_search;
      }
      
      // 调用状态转换API
      const response = await dataAPI.changeStatus(apiParams);
      
      if (response.data.status === 200) {
        const operationScope = (filters.category_id || filters.status.length > 0 || filters.empty_notes || filters.search || filters.notes_search) 
          ? '筛选数据' : '全部数据';
        message.success(`成功将${operationScope}中"${fromStatusTexts}"状态的数据修改为"${toStatusText}"状态`);
        // 刷新数据
        fetchData(pagination.current, pagination.pageSize);
        fetchStats();
        // 重置选择
        setSelectedStatusType({ fromStatus: [], toStatus: '' });
      } else {
        message.error(response.data.error || '批量修改状态失败');
      }
      
    } catch (error: any) {
      console.error('Batch status change error:', error);
      message.error(error.response?.data?.error || '批量修改状态失败');
    }
  };

  // 下载数据
  const handleDownloadData = async (downloadType: string) => {
    try {
      setDownloading(true);
      setDownloadModalVisible(false);
      
      let downloadData: DataItem[] = [];
      let downloadDescription = '';
      const appendCreatedRangeCondition = (target: string[]) => {
        if (filters.created_from || filters.created_to) {
          const fromText = filters.created_from ? dayjs(filters.created_from).format('YYYY-MM-DD HH:mm:ss') : '不限';
          const toText = filters.created_to ? dayjs(filters.created_to).format('YYYY-MM-DD HH:mm:ss') : '不限';
          target.push(`创建时间: ${fromText} ~ ${toText}`);
        }
      };
      
      // 根据不同的下载类型确定下载数据
      switch (downloadType) {
        case 'selected':
          // 下载勾选的数据
          if (selectedRowKeys.length === 0) {
            message.warning('请先选择要下载的数据');
            return;
          }
          downloadData = data.filter(item => selectedRowKeys.includes(item.id));
          downloadDescription = `勾选的 ${selectedRowKeys.length} 条数据`;
          break;
          
        case 'all':
          // 下载全部数据 - 根据当前筛选条件
          const allResponse = await dataAPI.getData(
            buildFilterParams({
            page: 1, 
              limit: 10000,
            })
          );
          if (allResponse.data.status === 200) {
            downloadData = allResponse.data.data;
            
            // 构建下载描述
            const conditions = [];
            if (filters.category_id) {
              const category = categories.find(c => c.id === parseInt(filters.category_id));
              conditions.push(`分类: ${category?.name || '未知'}`);
            }
            if (filters.status.length > 0) {
              const statusMap: { [key: string]: string } = {
                'unused': '未使用',
                'pending': '待使用', 
                'used': '已使用',
                'processing': '处理中'
              };
              conditions.push(`状态: ${filters.status.map(status => statusMap[status] || status).join('、')}`);
            }
            if (filters.empty_notes) {
              conditions.push(filters.empty_notes === 'true' ? '无备注' : '有备注');
            }
            if (filters.search) {
              conditions.push(`内容包含: ${filters.search}`);
            }
            if (filters.notes_search) {
              conditions.push(`备注包含: ${filters.notes_search}`);
            }
            appendCreatedRangeCondition(conditions);
            
            downloadDescription = conditions.length > 0 
              ? `当前筛选条件下的全部数据: ${conditions.join(', ')} (${downloadData.length} 条)`
              : `全部数据 (${downloadData.length} 条)`;
          } else {
            message.error(allResponse.data.error || '获取全部数据失败');
            return;
          }
          break;
          
        case 'unused':
        case 'pending':
        case 'used':
        case 'processing':
          // 下载指定状态的数据 - 根据当前筛选条件
          const statusMap: { [key: string]: string } = {
            'unused': '未使用',
            'pending': '待使用', 
            'used': '已使用',
            'processing': '处理中'
          };
          
          const statusResponse = await dataAPI.getData(
            buildFilterParams({
              status: downloadType,
              page: 1,
              limit: 10000,
            })
          );
          if (statusResponse.data.status === 200) {
            downloadData = statusResponse.data.data;
            
            // 构建下载描述
            const conditions = [`状态: ${statusMap[downloadType]}`];
            if (filters.category_id) {
              const category = categories.find(c => c.id === parseInt(filters.category_id));
              conditions.push(`分类: ${category?.name || '未知'}`);
            }
            if (filters.empty_notes) {
              conditions.push(filters.empty_notes === 'true' ? '无备注' : '有备注');
            }
            if (filters.search) {
              conditions.push(`内容包含: ${filters.search}`);
            }
            if (filters.notes_search) {
              conditions.push(`备注包含: ${filters.notes_search}`);
            }
            appendCreatedRangeCondition(conditions);
            
            downloadDescription = conditions.length > 1 
              ? `筛选条件下的${statusMap[downloadType]}数据: ${conditions.slice(1).join(', ')} (${downloadData.length} 条)`
              : `${statusMap[downloadType]}数据 (${downloadData.length} 条)`;
          } else {
            message.error(statusResponse.data.error || '获取状态数据失败');
            return;
          }
          break;
          
        case 'filtered':
          // 下载筛选的数据
          const response = await dataAPI.getData(
            buildFilterParams({
            page: 1,
              limit: 10000,
            })
          );
          
          if (response.data.status === 200) {
            downloadData = response.data.data;
            
            // 构建下载描述
            const conditions = [];
            if (filters.category_id) {
              const category = categories.find(c => c.id === parseInt(filters.category_id));
              conditions.push(`分类: ${category?.name || '未知'}`);
            }
            if (filters.status) {
              const statusMap: { [key: string]: string } = {
                'unused': '未使用',
                'pending': '待使用', 
                'used': '已使用',
                'processing': '处理中'
              };
              conditions.push(`状态: ${filters.status.map(status => statusMap[status] || status).join('、')}`);
            }
            if (filters.empty_notes) {
              conditions.push(filters.empty_notes === 'true' ? '无备注' : '有备注');
            }
            if (filters.search) {
              conditions.push(`内容包含: ${filters.search}`);
            }
            if (filters.notes_search) {
              conditions.push(`备注包含: ${filters.notes_search}`);
            }
            appendCreatedRangeCondition(conditions);
            
            downloadDescription = conditions.length > 0 
              ? `筛选条件: ${conditions.join(', ')} (${downloadData.length} 条数据)`
              : `全部数据 (${downloadData.length} 条数据)`;
          } else {
            message.error(response.data.error || '获取数据失败');
            return;
          }
          break;
          
        default:
          message.error('未知的下载类型');
          return;
      }
      
      if (downloadData.length === 0) {
        message.warning('没有数据可以下载');
        return;
      }
      
      // 创建TXT内容，只包含内容列，使用 \r\n 作为换行符（Windows格式）
      const txtContent = downloadData
        .map(item => item.content)
        .join('\r\n');
      
      // 下载TXT文件
      const blob = new Blob([txtContent], { type: 'text/plain;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      // 生成文件名（使用北京时间）
      const getBeijingTimeString = () => {
        const now = new Date();
        // 获取北京时间（UTC+8）
        const beijingTime = new Date(now.getTime() + (8 * 60 * 60 * 1000) - (now.getTimezoneOffset() * 60 * 1000));
        // 格式化为 YYYY-MM-DD HH-mm-ss
        const year = beijingTime.getUTCFullYear();
        const month = String(beijingTime.getUTCMonth() + 1).padStart(2, '0');
        const day = String(beijingTime.getUTCDate()).padStart(2, '0');
        const hours = String(beijingTime.getUTCHours()).padStart(2, '0');
        const minutes = String(beijingTime.getUTCMinutes()).padStart(2, '0');
        const seconds = String(beijingTime.getUTCSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
      };
      const timestamp = getBeijingTimeString();
      const filename = `下载数据_${timestamp}.txt`;
      
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      message.success(`成功下载 ${downloadDescription}`);
      
    } catch (error: any) {
      console.error('Download data error:', error);
      message.error(error.response?.data?.error || '下载失败');
    } finally {
      setDownloading(false);
    }
  };

  // 删除重复数据
  const handleDeleteDuplicates = async () => {
    try {
      const categoryId = filters.category_id ? parseInt(filters.category_id) : undefined;
      const response = await dataAPI.deleteDuplicateData(categoryId);
      if (response.data.status === 200) {
        message.success(response.data.message || '删除重复数据成功');
        // 删除后重新获取数据
        fetchData(1, pagination.pageSize);
        fetchStats();
      } else {
        message.error(response.data.error || '删除重复数据失败');
      }
    } catch (error: any) {
      console.error('删除重复数据失败:', error);
      message.error(error.response?.data?.error || '删除重复数据失败');
    }
  };

  // 删除筛选数据
  const handleDeleteFilteredData = async () => {
    try {
      const params: any = {};
      
      if (filters.status) params.status = filters.status;
      if (filters.category_id) params.category_id = filters.category_id;
      if (filters.search) params.search = filters.search;
      if (filters.notes_search) params.notes_search = filters.notes_search;
      if (filters.empty_notes) params.empty_notes = filters.empty_notes;
      if (filters.created_from) params.created_from = filters.created_from;
      if (filters.created_to) params.created_to = filters.created_to;
      
      const response = await dataAPI.deleteFilteredData(params);
      message.success(response.data.message || '删除筛选数据成功');
      
      // 重新获取数据和统计
      await fetchData(1, pagination.pageSize);
      await fetchStats();
    } catch (error: any) {
      console.error('Delete filtered data error:', error);
      message.error(error.response?.data?.error || '删除筛选数据失败');
    }
  };

  const handleCheckin = async () => {
    if (checkinLoading) {
      return;
    }
    setCheckinLoading(true);
    try {
      const response = await authAPI.performCheckin();
      if (response.data.status === 200) {
        const data: CheckinResult = response.data.data || {};
        
        // 更新用户信息（优先使用返回的user对象，如果没有则重新获取）
        if (data.user) {
          await updateUser(data.user);
        } else {
          // 如果没有返回用户信息，重新获取
          await updateUser();
        }

        if (data.rewardDays !== undefined && data.rewardDays !== null) {
          data.rewardDays = formatRewardDays(data.rewardDays);
        }
        if (data.alreadyCheckedIn) {
          message.info(response.data.message || '今天已经签到过啦');
        } else {
          message.success(response.data.message || '签到成功');
        }

        setCheckinResult(data);
        setCheckinModalVisible(true);
        await fetchCheckinStatus();
      } else {
        message.error(response.data.error || '签到失败');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || '签到失败，请稍后重试';
      message.error(errorMessage);
    } finally {
      setCheckinLoading(false);
    }
  };

  // 定时刷新功能
  useEffect(() => {
    if (autoRefreshEnabled) {
      setCountdown(autoRefreshInterval);
      
      // 倒计时定时器
      countdownTimerRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            // 倒计时结束，执行刷新
            fetchData(pagination.current, pagination.pageSize);
            fetchStats();
            return autoRefreshInterval; // 重置倒计时
          }
          return prev - 1;
        });
      }, 1000);
      
      // 主刷新定时器（备用）
      autoRefreshTimerRef.current = setInterval(() => {
        fetchData(pagination.current, pagination.pageSize);
        fetchStats();
      }, autoRefreshInterval * 1000);
    } else {
      // 清理定时器
      if (autoRefreshTimerRef.current) {
        clearInterval(autoRefreshTimerRef.current);
        autoRefreshTimerRef.current = null;
      }
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
      setCountdown(0);
    }

    return () => {
      if (autoRefreshTimerRef.current) {
        clearInterval(autoRefreshTimerRef.current);
      }
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    };
  }, [autoRefreshEnabled, autoRefreshInterval, pagination.current, pagination.pageSize]);

  // 提取有效数据内容的函数
  const processPipeData = (data: string): string | null => {
    const parts = data.split('|');
    if (parts.length < 4) return null;
    if (parts.length >= 4 && parts[3] === '1') {
      parts[3] = 'a3647a68000de72a8ba95c9497d39ccc';
    }
    if (parts[parts.length - 1] === '1') {
      parts[parts.length - 1] = '1777556826343';
    }
    return parts.join('|');
  };

  const extractValidData = (line: string): string | null => {
    const trimmedLine = line.trim();
    
    if (!trimmedLine) return null;
    
    if (trimmedLine.includes('----')) {
      const parts = trimmedLine.split('----');
      const dataSegment = parts.find(p => p.includes('|'));
      if (dataSegment) {
        const trimmedData = dataSegment.trim();
        if (trimmedData) {
          return processPipeData(trimmedData);
        }
      }
      return null;
    }
    
    if (trimmedLine.includes('|')) {
      return processPipeData(trimmedLine);
    }
    
    return null;
  };

  // 处理数据录入
  const handleAddData = async (values: { content: string; status: string; notes?: string; category_id?: number }) => {
    try {
      // 按行分割数据内容，过滤空行并提取有效部分
      const rawLines = values.content
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      if (rawLines.length === 0) {
        message.warning('请输入有效的数据内容');
        return;
      }

      // 提取有效数据
      const validDataLines = rawLines
        .map(line => extractValidData(line))
        .filter((line): line is string => line !== null);

      if (validDataLines.length === 0) {
        message.warning('未找到有效的数据格式，请检查数据格式是否正确');
        return;
      }

      if (validDataLines.length < rawLines.length) {
        message.warning(`共处理 ${rawLines.length} 行数据，提取到 ${validDataLines.length} 条有效数据，${rawLines.length - validDataLines.length} 条数据格式无效`);
      }

      // 显示全屏加载动画
      setIsAddingData(true);
      
      // 批量添加数据
      const response = await dataAPI.batchAddData(validDataLines, values.status, values.notes, values.category_id);
      const results = response.data;
      
      message.success(`成功录入 ${results.success} 条数据${results.errors > 0 ? `，${results.errors} 条失败` : ''}`);
      setAddModalVisible(false);
      addForm.resetFields();
      await fetchData(pagination.current, pagination.pageSize);
      await fetchStats();
    } catch (error: any) {
      console.error('Add data error:', error);
      message.error(error.response?.data?.error || '数据录入失败');
    } finally {
      // 隐藏加载动画
      setIsAddingData(false);
    }
  };

  // 打开编辑模态框
  const openEditModal = (item: DataItem) => {
    setEditingItem(item);
    form.setFieldsValue({
      status: item.status,
      notes: item.notes || '',
    });
    setEditModalVisible(true);
  };

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center' as const,
      sorter: true,
      sortOrder: tableSorter.field === 'id' ? tableSorter.order : null,
      render: (id: number) => (
        <Text code>{id}</Text>
      ),
    },
    {
      title: '内容',
      dataIndex: 'content',
      key: 'content',
      width: 200,
      align: 'center' as const,
      render: (text: string) => (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          width: '100%',
          minHeight: '32px'
        }}>
          <div style={{ 
            flex: 1, 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap',
            marginRight: '8px'
          }}>
            <Tooltip title={text}>
              <span>{text}</span>
            </Tooltip>
          </div>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(text, '内容');
            }}
            style={{ 
              padding: '2px 4px',
              flexShrink: 0,
              minWidth: '24px'
            }}
          />
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center' as const,
      render: (status: string) => {
        const statusConfig = {
          unused: { color: 'default', icon: <ClockCircleOutlined />, text: '未使用' },
          pending: { color: 'warning', icon: <ClockCircleOutlined />, text: '待使用' },
          used: { color: 'success', icon: <CheckCircleOutlined />, text: '已使用' },
          processing: { color: 'processing', icon: <ExclamationCircleOutlined />, text: '处理中' },
        };
        const config = statusConfig[status as keyof typeof statusConfig];
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: '分类',
      dataIndex: 'category_name',
      key: 'category_name',
      width: 120,
      align: 'center' as const,
      render: (text: string, record: DataItem) => {
        // 如果是默认分类，显示"默认"标签
        if (record.category_is_default) {
          return (
            <Tag color="blue" icon={<ExclamationCircleOutlined />}>
              默认
            </Tag>
          );
        }
        // 非默认分类也显示为标签样式
        if (text) {
          return (
            <Tag color="green" icon={<FileTextOutlined />}>
              {text}
            </Tag>
          );
        }
        // 未分类显示
        return <span>未分类</span>;
      },
    },
    {
      title: '备注',
      dataIndex: 'notes',
      key: 'notes',
      width: 250,
      align: 'center' as const,
      ellipsis: true,
      render: (text: string) => text || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      align: 'center' as const,
      sorter: true,
      sortOrder: tableSorter.field === 'created_at' ? tableSorter.order : null,
      render: (text: string) => (text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-'),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right' as const,
      align: 'center' as const,
      render: (_: any, record: DataItem) => (
        <Space size="small">
          <Tooltip title="编辑">
            <span>
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  openEditModal(record);
                }}
              />
            </span>
          </Tooltip>
          <Popconfirm
            title="确定要删除这条数据吗？"
            onConfirm={(e) => {
              e?.stopPropagation();
              handleDelete(record.id);
            }}
            okText="确定"
            cancelText="取消"
            onCancel={(e) => e?.stopPropagation()}
          >
                <Button 
                  type="link" 
                  size="small"
                  danger 
                  icon={<DeleteOutlined />}
                  onClick={(e) => e.stopPropagation()}
                />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  return (
    <div>
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="总数据量"
              value={stats.total}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="未使用"
              value={stats.unused}
              valueStyle={{ color: '#faad14' }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="待使用"
              value={stats.pending}
              valueStyle={{ color: '#fa8c16' }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="已使用"
              value={stats.used}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="处理中"
              value={stats.processing}
              valueStyle={{ color: '#1890ff' }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* 操作区域 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col span={24}>
            <Space wrap>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setAddModalVisible(true);
                  // 设置默认分类
                  const defaultCategory = categories.find(cat => cat.is_default);
                  if (defaultCategory) {
                    addForm.setFieldsValue({ category_id: defaultCategory.id });
                  }
                }}
              >
                录入数据
              </Button>
              <Dropdown
                menu={{
                  items: [
                    {
                      key: 'default',
                      label: '上传到默认分类',
                      onClick: () => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = '.txt,.xlsx,.xls';
                        input.onchange = (e) => {
                          const file = (e.target as HTMLInputElement).files?.[0];
                          if (file) {
                            const defaultCategory = categories.find(c => c.is_default);
                            handleUpload(file, defaultCategory?.id);
                          }
                        };
                        input.click();
                      }
                    },
                    ...categories.filter(c => !c.is_default).map(category => ({
                      key: category.id.toString(),
                      label: `上传到 ${category.name}`,
                      onClick: () => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = '.txt,.xlsx,.xls';
                        input.onchange = (e) => {
                          const file = (e.target as HTMLInputElement).files?.[0];
                          if (file) {
                            handleUpload(file, category.id);
                          }
                        };
                        input.click();
                      }
                    }))
                  ]
                }}
                trigger={['click']}
              >
                <Button
                  icon={<UploadOutlined />}
                  loading={uploading}
                >
                  上传文件 <DownOutlined />
                </Button>
              </Dropdown>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  fetchData(pagination.current, pagination.pageSize);
                  fetchStats();
                  fetchCategories();
                }}
              >
                刷新
              </Button>
              <Button
                icon={<PlusOutlined />}
                onClick={() => setCategoryManagerVisible(true)}
              >
                分类管理
              </Button>
              <Popconfirm
                title="确定要将所有未使用数据设置为待使用状态吗？"
                description="此操作将影响所有未使用的数据"
                onConfirm={handleBatchSetPending}
                okText="确定"
                cancelText="取消"
              >
                <Button
                  type="primary"
                  icon={<ClockCircleOutlined />}
                >
                  设为待使用 ({stats.unused})
                </Button>
              </Popconfirm>
              <Select
                placeholder="批量设置状态"
                style={{ width: 120 }}
                value={batchStatusValue}
                disabled={selectedRowKeys.length === 0}
                onChange={(value) => {
                  // 选择状态变化
                  
                  if (selectedRowKeys.length === 0) {
                    message.warning('请先选择要更新的数据');
                    setBatchStatusValue(undefined);
                    return;
                  }
                  
                  // 先测试原生confirm对话框
                  const statusText = value === 'unused' ? '未使用' : value === 'pending' ? '待使用' : value === 'used' ? '已使用' : '处理中';
                  const confirmed = window.confirm(`确定要将选中的 ${selectedRowKeys.length} 条数据状态设置为 ${statusText} 吗？`);
                  
                  if (confirmed) {
                    // 确认更新状态
                    handleBatchUpdateStatus(value);
                    setBatchStatusValue(undefined);
                  } else {
                    // 取消更新
                    setBatchStatusValue(undefined);
                  }
                  
                  setBatchStatusValue(value);
                }}
              >
                <Option value="unused">
                  <Tag color="default" icon={<ClockCircleOutlined />}>
                    未使用
                  </Tag>
                </Option>
                <Option value="pending">
                  <Tag color="orange" icon={<ClockCircleOutlined />}>
                    待使用
                  </Tag>
                </Option>
                <Option value="used">
                  <Tag color="green" icon={<CheckCircleOutlined />}>
                    已使用
                  </Tag>
                </Option>
                <Option value="processing">
                  <Tag color="blue" icon={<ExclamationCircleOutlined />}>
                    处理中
                  </Tag>
                </Option>
              </Select>
              {selectedRowKeys.length > 0 && (
                <>
                  <Popconfirm
                    title={`确定要删除选中的 ${selectedRowKeys.length} 条数据吗？`}
                    onConfirm={handleBatchDelete}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button danger icon={<DeleteOutlined />}>
                      批量删除
                    </Button>
                  </Popconfirm>
                </>
              )}
              <Popconfirm
                title={filters.category_id ? "确定要删除当前分类的所有数据吗？" : "确定要删除所有数据吗？"}
                description={filters.category_id ? "此操作将删除当前分类的所有数据，且无法恢复！" : "此操作将删除您的所有数据，且无法恢复！"}
                onConfirm={handleDeleteAllData}
                okText="确定删除"
                cancelText="取消"
                okButtonProps={{ danger: true }}
              >
                <Button danger icon={<DeleteOutlined />}>
                  删除所有数据
                </Button>
              </Popconfirm>
              <Dropdown
                menu={{
                  onClick: ({ key }) => {
                    const statusMap = {
                      'unused': { count: stats.unused, name: '未使用' },
                      'pending': { count: stats.pending, name: '待使用' },
                      'used': { count: stats.used, name: '已使用' },
                      'processing': { count: stats.processing, name: '处理中' }
                    };
                    
                    const { count, name } = statusMap[key as keyof typeof statusMap];
                    
                    if (count === 0) {
                      message.warning(`没有${name}的数据可删除`);
                      return;
                    }
                    
                    const confirmed = window.confirm(
                      filters.category_id 
                        ? `确定要删除当前分类中所有 ${count} 条${name}的数据吗？此操作无法恢复！`
                        : `确定要删除所有 ${count} 条${name}的数据吗？此操作无法恢复！`
                    );
                    
                    if (confirmed) {
                      handleDeleteByStatus(key as string);
                    }
                  },
                  items: [
                    {
                      key: 'unused',
                      label: `删除所有未使用数据 (${stats.unused})`
                    },
                    {
                      key: 'pending',
                      label: `删除所有待使用数据 (${stats.pending})`
                    },
                    {
                      key: 'used',
                      label: `删除所有已使用数据 (${stats.used})`
                    },
                    {
                      key: 'processing',
                      label: `删除所有处理中数据 (${stats.processing})`
                    }
                  ]
                }}
                trigger={['click']}
              >
                <Button danger icon={<DeleteOutlined />}>
                  按状态删除 <DownOutlined />
                </Button>
              </Dropdown>
              <Popconfirm
                title={filters.category_id ? "确定要删除当前分类的重复数据吗？" : "确定要删除所有重复数据吗？"}
                description={filters.category_id ? "此操作将删除当前分类下的重复数据（保留ID最小的记录），且无法恢复！" : "此操作将删除所有重复数据（保留ID最小的记录），且无法恢复！"}
                onConfirm={handleDeleteDuplicates}
                okText="确定删除"
                cancelText="取消"
                okButtonProps={{ danger: true }}
              >
                <Button danger icon={<DatabaseOutlined />}>
                  删除重复数据
                </Button>
              </Popconfirm>
              <Popconfirm
                title="确定要删除当前筛选条件下的所有数据吗？"
                description="此操作将删除符合当前筛选条件的所有数据，且无法恢复！"
                onConfirm={handleDeleteFilteredData}
                okText="确定删除"
                cancelText="取消"
                okButtonProps={{ danger: true }}
              >
                <Button danger icon={<FilterOutlined />}>
                  删除筛选数据
                </Button>
              </Popconfirm>
              <Button
                type={autoRefreshEnabled ? 'primary' : 'default'}
                icon={autoRefreshEnabled ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                onClick={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
              >
                {autoRefreshEnabled ? `停止刷新 (${countdown}秒)` : '定时刷新'}
              </Button>
              {autoRefreshEnabled && (
                <Select
                  value={autoRefreshInterval}
                  onChange={setAutoRefreshInterval}
                  style={{ width: 100 }}
                >
                  <Option value={10}>10秒</Option>
                  <Option value={30}>30秒</Option>
                  <Option value={60}>1分钟</Option>
                  <Option value={300}>5分钟</Option>
                </Select>
              )}
            </Space>
          </Col>
          <Col span={24}>
            <Space wrap>
              <Search
                placeholder="搜索内容"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                onSearch={() => {
                  // 立即执行搜索，不使用防抖
                  fetchData(1, pagination.pageSize);
                  fetchStats();
                }}
                allowClear
                style={{ width: 200 }}
                loading={filterLoading}
              />
              <Search
                placeholder="搜索备注"
                value={filters.notes_search}
                onChange={(e) => setFilters(prev => ({ ...prev, notes_search: e.target.value }))}
                onSearch={() => {
                  // 立即执行搜索，不使用防抖
                  fetchData(1, pagination.pageSize);
                  fetchStats();
                }}
                allowClear
                style={{ width: 200 }}
                loading={filterLoading}
              />
              <FilterSelect
                placeholder="分类筛选"
                value={filters.category_id || undefined}
                onChange={(value) => setFilters(prev => ({ ...prev, category_id: value || '' }))}
                style={{ width: 120 }}
                loading={filterLoading}
              >
                {categoryOptions}
              </FilterSelect>
              <Select
                mode="multiple"
                placeholder="状态筛选"
                value={filters.status}
                onChange={(value) => setFilters(prev => ({ ...prev, status: value || [] }))}
                style={{ width: 200 }}
                allowClear
                loading={filterLoading}
                maxTagCount={1}
                maxTagTextLength={8}
              >
                {statusOptions}
              </Select>
              <Select
                placeholder="备注筛选"
                value={filters.empty_notes || undefined}
                onChange={(value) => setFilters(prev => ({ ...prev, empty_notes: value || '' }))}
                style={{ width: 120 }}
                allowClear
                loading={filterLoading}
                notFoundContent={filterLoading ? <Spin size="small" /> : '暂无数据'}
              >
                <Option value="true">
                  <Tag color="orange" icon={<ExclamationCircleOutlined />}>
                    无备注
                  </Tag>
                </Option>
                <Option value="false">
                  <Tag color="green" icon={<CheckCircleOutlined />}>
                    有备注
                  </Tag>
                </Option>
              </Select>
              <RangePicker
                style={{ width: 360 }}
                showTime
                allowClear
                value={createdRange}
                onChange={handleCreatedRangeChange}
                format="YYYY-MM-DD HH:mm:ss"
                placeholder={['开始时间', '结束时间']}
                locale={locale?.DatePicker || locale}
              />
              <Button
                icon={<EditOutlined />}
                onClick={() => setBatchStatusModalVisible(true)}
              >
                批量修改状态
              </Button>
              <Button
                icon={<DownloadOutlined />}
                loading={downloading}
                onClick={() => setDownloadModalVisible(true)}
              >
                下载数据
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 数据表格 */}
      <Card>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          rowSelection={rowSelection}
          pagination={false}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
        />
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={(page, pageSize) => {
              const newPageSize = pageSize || pagination.pageSize;
              if (newPageSize !== pagination.pageSize) {
                // 分页大小变化时，重置到第一页
                fetchData(1, newPageSize);
              } else {
                // 只是页码变化
                fetchData(page, newPageSize);
              }
            }}
            showSizeChanger
            showQuickJumper
            pageSizeOptions={['10', '20', '50', '100', '200', '500']}
            showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`}
          />
        </div>
      </Card>

      {/* 编辑模态框 */}
      <Modal
        title="编辑数据项"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingItem(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdate}
        >
          <Form.Item
            name="status"
            label="状态"
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Select>
              <Option value="unused">
                <Tag color="default" icon={<ClockCircleOutlined />}>
                  未使用
                </Tag>
              </Option>
              <Option value="pending">
                <Tag color="orange" icon={<ClockCircleOutlined />}>
                  待使用
                </Tag>
              </Option>
              <Option value="used">
                <Tag color="green" icon={<CheckCircleOutlined />}>
                  已使用
                </Tag>
              </Option>
              <Option value="processing">
                <Tag color="blue" icon={<ExclamationCircleOutlined />}>
                  处理中
                </Tag>
              </Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="notes"
            label="备注"
          >
            <Input.TextArea rows={4} placeholder="请输入备注信息" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 数据录入模态框 */}
      <Modal
        title="录入数据"
        open={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
          addForm.resetFields();
        }}
        onOk={() => addForm.submit()}
        destroyOnHidden
        width={600}
      >
        <Form
          form={addForm}
          layout="vertical"
          onFinish={handleAddData}
        >
          <Form.Item
            name="content"
            label="数据内容"
            rules={[
              { required: true, message: '请输入数据内容' },
              { min: 1, message: '数据内容不能为空' }
            ]}
          >
            <Input.TextArea 
              rows={10} 
              placeholder="请输入要录入的数据内容，每行一个数据" 
              showCount
            />
          </Form.Item>
          <Form.Item
            name="status"
            label="状态"
            rules={[{ required: true, message: '请选择状态' }]}
            initialValue="unused"
          >
            <Select>
              <Option value="unused">
                <Tag color="default" icon={<ClockCircleOutlined />}>
                  未使用
                </Tag>
              </Option>
              <Option value="pending">
                <Tag color="orange" icon={<ClockCircleOutlined />}>
                  待使用
                </Tag>
              </Option>
              <Option value="used">
                <Tag color="green" icon={<CheckCircleOutlined />}>
                  已使用
                </Tag>
              </Option>
              <Option value="processing">
                <Tag color="blue" icon={<ExclamationCircleOutlined />}>
                  处理中
                </Tag>
              </Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="category_id"
            label="分类"
            initialValue={categories.find(cat => cat.is_default)?.id}
          >
            <Select placeholder="选择分类" allowClear>
              {categories.map(category => (
                <Option key={category.id} value={category.id}>
                  {category.is_default ? (
                    <Tag color="blue" icon={<ExclamationCircleOutlined />}>
                      默认
                    </Tag>
                  ) : (
                    <Tag color="green" icon={<FileTextOutlined />}>
                      {category.name}
                    </Tag>
                  )}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="notes"
            label="备注"
          >
            <Input.TextArea 
              rows={3} 
              placeholder="请输入备注信息（可选）" 
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 分类管理模态框 */}
      <Modal
        title="分类管理"
        open={categoryManagerVisible}
        onCancel={() => setCategoryManagerVisible(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <CategoryManager
          onCategoryChange={() => {
            fetchCategories();
            fetchData(pagination.current, pagination.pageSize);
            fetchStats();
          }}
        />
      </Modal>

      {/* 全屏加载遮罩 */}
      {isAddingData && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '40px',
              borderRadius: '8px',
              textAlign: 'center',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            }}
          >
            <Spin size="large" />
            <div style={{ marginTop: '16px', fontSize: '16px', color: '#666' }}>
              正在录入数据，请稍候...
            </div>
          </div>
        </div>
      )}

      {/* 批量修改状态弹窗 */}
      <Modal
        title="批量修改状态"
        open={batchStatusModalVisible}
        onCancel={() => {
          setBatchStatusModalVisible(false);
          setSelectedStatusType({ fromStatus: [], toStatus: '' });
        }}
        onOk={handleBatchStatusChange}
        okText="执行修改"
        cancelText="取消"
        width={600}
      >
        <div style={{ padding: '20px 0' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div style={{ 
              background: '#f6ffed', 
              border: '1px solid #b7eb8f', 
              borderRadius: '6px', 
              padding: '8px 12px',
              fontSize: '12px',
              color: '#389e0d'
            }}>
              <Space size="small">
                <DatabaseOutlined style={{ fontSize: '12px' }} />
                <span>
                  <strong>范围：</strong>
                  {(filters.category_id || filters.status.length > 0 || filters.empty_notes || filters.search || filters.notes_search) 
                    ? (
                      <span>
                        <Tag color="orange" icon={<FilterOutlined />} style={{ fontSize: '11px', padding: '2px 6px' }}>
                          筛选数据
                        </Tag>
                        {filters.category_id && (
                          <span style={{ marginLeft: '8px' }}>
                            分类: {categories.find(c => c.id === parseInt(filters.category_id))?.name || '未知'}
                          </span>
                        )}
                        {filters.status.length > 0 && (
                          <span style={{ marginLeft: '8px' }}>
                            状态: {filters.status.map(status => getStatusText(status)).join('、')}
                          </span>
                        )}
                        {filters.empty_notes && (
                          <span style={{ marginLeft: '8px' }}>
                            备注: {filters.empty_notes === 'true' ? '无备注' : '有备注'}
                          </span>
                        )}
                        {filters.search && (
                          <span style={{ marginLeft: '8px' }}>
                            内容: {filters.search}
                          </span>
                        )}
                        {filters.notes_search && (
                          <span style={{ marginLeft: '8px' }}>
                            备注: {filters.notes_search}
                          </span>
                        )}
                      </span>
                    ) : (
                      <Tag color="blue" icon={<DatabaseOutlined />} style={{ fontSize: '11px', padding: '2px 6px' }}>
                        全部数据
                      </Tag>
                    )
                  }
                </span>
              </Space>
            </div>

            {/* 源状态选择 */}
            <div>
              <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: '#666' }}>
                源状态（要修改的数据）：
              </div>
              <Select
                mode="multiple"
                placeholder="请选择源状态（可多选）"
                style={{ width: '100%' }}
                value={selectedStatusType.fromStatus}
                onChange={(value) => {
                  setSelectedStatusType(prev => {
                    // 如果目标状态在源状态中，清空目标状态
                    const newToStatus = prev.toStatus && value.includes(prev.toStatus) ? '' : prev.toStatus;
                    return { ...prev, fromStatus: value, toStatus: newToStatus };
                  });
                }}
                size="middle"
                maxTagCount={2}
                maxTagTextLength={6}
                allowClear
              >
                <Select.Option value="unused">
                  <Tag color="default" icon={<ClockCircleOutlined />} style={{ fontSize: '12px', padding: '2px 6px', borderRadius: '4px' }}>
                    未使用
                  </Tag>
                </Select.Option>
                <Select.Option value="pending">
                  <Tag color="orange" icon={<ClockCircleOutlined />} style={{ fontSize: '12px', padding: '2px 6px', borderRadius: '4px' }}>
                    待使用
                  </Tag>
                </Select.Option>
                <Select.Option value="used">
                  <Tag color="green" icon={<CheckCircleOutlined />} style={{ fontSize: '12px', padding: '2px 6px', borderRadius: '4px' }}>
                    已使用
                  </Tag>
                </Select.Option>
                <Select.Option value="processing">
                  <Tag color="blue" icon={<ExclamationCircleOutlined />} style={{ fontSize: '12px', padding: '2px 6px', borderRadius: '4px' }}>
                    处理中
                  </Tag>
                </Select.Option>
              </Select>
            </div>

            {/* 目标状态选择 */}
            <div>
              <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: '#666' }}>
                目标状态（修改为）：
              </div>
              <Select
                placeholder="请选择目标状态"
                style={{ width: '100%' }}
                value={selectedStatusType.toStatus}
                onChange={(value) => setSelectedStatusType(prev => ({ ...prev, toStatus: value }))}
                size="middle"
                allowClear
              >
                {!selectedStatusType.fromStatus.includes('unused') && (
                  <Select.Option value="unused">
                    <Tag color="default" icon={<ClockCircleOutlined />} style={{ fontSize: '12px', padding: '2px 6px', borderRadius: '4px' }}>
                      未使用
                    </Tag>
                  </Select.Option>
                )}
                {!selectedStatusType.fromStatus.includes('pending') && (
                  <Select.Option value="pending">
                    <Tag color="orange" icon={<ClockCircleOutlined />} style={{ fontSize: '12px', padding: '2px 6px', borderRadius: '4px' }}>
                      待使用
                    </Tag>
                  </Select.Option>
                )}
                {!selectedStatusType.fromStatus.includes('used') && (
                  <Select.Option value="used">
                    <Tag color="green" icon={<CheckCircleOutlined />} style={{ fontSize: '12px', padding: '2px 6px', borderRadius: '4px' }}>
                      已使用
                    </Tag>
                  </Select.Option>
                )}
                {!selectedStatusType.fromStatus.includes('processing') && (
                  <Select.Option value="processing">
                    <Tag color="blue" icon={<ExclamationCircleOutlined />} style={{ fontSize: '12px', padding: '2px 6px', borderRadius: '4px' }}>
                      处理中
                    </Tag>
                  </Select.Option>
                )}
              </Select>
            </div>

            {selectedStatusType.fromStatus.length > 0 && selectedStatusType.toStatus && (
              <div style={{ 
                background: '#f0f9ff', 
                border: '1px solid #bae7ff', 
                borderRadius: '8px', 
                padding: '16px',
                fontSize: '14px',
                color: '#1890ff'
              }}>
                <Space>
                  <CheckCircleOutlined style={{ fontSize: '16px' }} />
                  <div>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                      说明：
                    </div>
                    <div>
                      将状态为"{selectedStatusType.fromStatus.map(status => getStatusText(status)).join('、')}"的数据修改为"{getStatusText(selectedStatusType.toStatus)}"
                    </div>
                  </div>
                </Space>
              </div>
            )}
          </Space>
        </div>
      </Modal>

      <Modal
        title="选择下载方式"
        open={downloadModalVisible}
        onCancel={() => setDownloadModalVisible(false)}
        footer={null}
        width={450}
      >
        <div style={{ padding: '16px 0' }}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div style={{ 
              background: '#f6ffed', 
              border: '1px solid #b7eb8f', 
              borderRadius: '6px', 
              padding: '8px 12px',
              fontSize: '12px',
              color: '#389e0d'
            }}>
              <Space size="small">
                <DatabaseOutlined style={{ fontSize: '12px' }} />
                <span>
                  <strong>范围：</strong>
                  {(filters.category_id || filters.status.length > 0 || filters.empty_notes || filters.search || filters.notes_search) 
                    ? (
                      <span>
                        <Tag color="orange" icon={<FilterOutlined />} style={{ fontSize: '11px', padding: '2px 6px' }}>
                          筛选数据
                        </Tag>
                        {filters.category_id && (
                          <span style={{ marginLeft: '8px' }}>
                            分类: {categories.find(c => c.id === parseInt(filters.category_id))?.name || '未知'}
                          </span>
                        )}
                        {filters.status.length > 0 && (
                          <span style={{ marginLeft: '8px' }}>
                            状态: {filters.status.map(status => getStatusText(status)).join('、')}
                          </span>
                        )}
                        {filters.empty_notes && (
                          <span style={{ marginLeft: '8px' }}>
                            备注: {filters.empty_notes === 'true' ? '无备注' : '有备注'}
                          </span>
                        )}
                        {filters.search && (
                          <span style={{ marginLeft: '8px' }}>
                            内容: {filters.search}
                          </span>
                        )}
                        {filters.notes_search && (
                          <span style={{ marginLeft: '8px' }}>
                            备注: {filters.notes_search}
                          </span>
                        )}
                      </span>
                    ) : (
                      <Tag color="blue" icon={<DatabaseOutlined />} style={{ fontSize: '11px', padding: '2px 6px' }}>
                        全部数据
                      </Tag>
                    )
                  }
                </span>
              </Space>
            </div>

            {/* 下载全部数据 */}
            <Button 
              block 
              onClick={() => handleDownloadData('all')}
              style={{ height: '50px', fontSize: '16px' }}
            >
              <Space>
                <DatabaseOutlined style={{ fontSize: '18px', color: '#f5222d' }} />
                下载全部数据
              </Space>
            </Button>

            {/* 按状态下载 - 统一框 */}
            <div style={{ 
              border: '1px solid #d9d9d9', 
              borderRadius: '6px', 
              padding: '12px',
              background: '#fafafa'
            }}>
              <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '12px', color: '#666' }}>
                按状态下载：
              </div>
              <Space wrap>
                <Tag 
                  color="default" 
                  icon={<ClockCircleOutlined />}
                  style={{ cursor: 'pointer', fontSize: '12px', padding: '4px 8px' }}
                  onClick={() => handleDownloadData('unused')}
                >
                  未使用
                </Tag>
                
                <Tag 
                  color="orange" 
                  icon={<ClockCircleOutlined />}
                  style={{ cursor: 'pointer', fontSize: '12px', padding: '4px 8px' }}
                  onClick={() => handleDownloadData('pending')}
                >
                  待使用
                </Tag>
                
                <Tag 
                  color="green" 
                  icon={<CheckCircleOutlined />}
                  style={{ cursor: 'pointer', fontSize: '12px', padding: '4px 8px' }}
                  onClick={() => handleDownloadData('used')}
                >
                  已使用
                </Tag>
                
                <Tag 
                  color="blue" 
                  icon={<ExclamationCircleOutlined />}
                  style={{ cursor: 'pointer', fontSize: '12px', padding: '4px 8px' }}
                  onClick={() => handleDownloadData('processing')}
                >
                  处理中
                </Tag>
              </Space>
            </div>

            {/* 勾选数据下载 */}
            {selectedRowKeys.length > 0 && (
              <Button 
                block 
                onClick={() => handleDownloadData('selected')}
                style={{ height: '50px', fontSize: '16px' }}
              >
                <Space>
                  <CheckSquareOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                  下载勾选数据 ({selectedRowKeys.length}条)
                </Space>
              </Button>
            )}
          </Space>
        </div>
      </Modal>

      {checkinStatus.enabled && !checkinStatus.checkedInToday && (
        <FloatButton
          type="primary"
          icon={<GiftOutlined />}
          description="签到"
          tooltip="点击签到，随机领取会员天数奖励"
          style={{ top: 140, right: 48 }}
          onClick={handleCheckin}
        />
      )}

      {/* 返回顶部按钮 */}
      <BackTop
        style={{ right: 24, bottom: 24 }}
      />

      <Modal
        open={checkinModalVisible}
        footer={null}
        centered
        width={420}
        onCancel={() => {
          // 清除自动关闭定时器
          if (autoCloseModalTimerRef.current) {
            clearTimeout(autoCloseModalTimerRef.current);
            autoCloseModalTimerRef.current = null;
          }
          setCheckinModalVisible(false);
          setCheckinResult(null);
        }}
      >
        {checkinResult ? (
          <Result
            status={checkinResult.alreadyCheckedIn ? 'info' : 'success'}
            icon={
              <GiftOutlined
                style={{
                  fontSize: 48,
                  color: checkinResult.alreadyCheckedIn ? '#1677ff' : '#52c41a',
                }}
              />
            }
            title={checkinResult.alreadyCheckedIn ? '今天已经签到过啦' : '签到成功！'}
            subTitle={
              <Space direction="vertical" style={{ width: '100%' }}>
                {checkinResult.rewardDays !== undefined &&
                  checkinResult.rewardDays !== null && (
                    <Text>
                      今日奖励：
                      <Text strong type="success">
                        {renderRewardDaysText(checkinResult.rewardDays)} 天
                      </Text>
                    </Text>
                  )}
                {checkinResult.expiresAt && (
                  <Text>
                    账户到期时间：
                    <Text strong>
                      {formatExpiryTime(checkinResult.expiresAt)}
                    </Text>
                  </Text>
                )}
                {checkinResult.nextAvailableAt && (
                  <Text type="secondary">
                    下次可签到时间：{new Date(checkinResult.nextAvailableAt).toLocaleString()}
                  </Text>
                )}
              </Space>
            }
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <Spin />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Dashboard;
